import React, { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from '../context/AuthContext';
import api from "../utils/api";
import { stateCityData } from './data';
import "./BookAppointment.css";

const BookAppointment = () => {
  const { patientUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { doctorId, state: initialState, city: initialCity } = location.state || {};

  const [formData, setFormData] = useState({
    name: '',
    age: '',
    gender: '',
    state: initialState || '',
    city: initialCity || '',
    doctor: doctorId || '',
    date: '',
    timeSlot: '',
    specificTime: '',
    notes: '',
  });

  const [cities, setCities] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [availability, setAvailability] = useState({});
  const [timeSlots, setTimeSlots] = useState([]);
  const [specificTimes, setSpecificTimes] = useState([]);
  const [platformFeeRate, setPlatformFeeRate] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!patientUser || !patientUser.id || patientUser.role !== "patient") {
      setError("You must be logged in as a patient to book an appointment.");
      return;
    }

    console.log('patientUser:', patientUser); // Debug log to verify patientUser

    if (initialState && initialCity) {
      setCities(stateCityData[initialState] || []);
      fetchDoctors(initialState, initialCity);
    }
    fetchPlatformSettings();
  }, [patientUser, initialState, initialCity]);

  useEffect(() => {
    if (formData.doctor && formData.date) {
      const day = new Date(formData.date).toLocaleString('en-US', { weekday: 'long' });
      fetchDoctorAvailability(formData.doctor, day);
    } else {
      setAvailability({});
      setTimeSlots([]);
      setSpecificTimes([]);
    }
  }, [formData.doctor, formData.date]);

  const fetchPlatformSettings = async () => {
    if (!patientUser || !patientUser.accessToken) {
      setError("Authentication required to fetch platform settings.");
      return;
    }
    try {
      const response = await api.get('/appointments/platform-settings', {
        headers: { Authorization: `Bearer ${patientUser.accessToken}` },
      });
      const settings = response.data;
      setPlatformFeeRate(settings.patientCommission / 100);
      setError(''); // Clear any previous errors on success
    } catch (err) {
      console.error('Failed to fetch platform settings:', err.response || err);
      setError('Failed to fetch platform settings. Using default fee.');
      setPlatformFeeRate(0.3); // Default to 30% if fetch fails
    }
  };

  const fetchDoctors = async (state, city) => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/doctors', {
        params: { state, city },
        headers: { Authorization: `Bearer ${patientUser.accessToken}` },
      });
      setDoctors(response.data);
    } catch (error) {
      setError('Failed to fetch doctors. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctorAvailability = async (doctorId, day) => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/doctors/${doctorId}/availability`, {
        params: { day },
        headers: { Authorization: `Bearer ${patientUser.accessToken}` },
      });
      const fetchedAvailability = response.data || {};
      const normalizedDay = day.charAt(0).toUpperCase() + day.slice(1).toLowerCase();
      const slots = fetchedAvailability[normalizedDay] || [];
      setAvailability(fetchedAvailability);
      setTimeSlots(slots);
      setSpecificTimes([]);
    } catch (error) {
      setError('Failed to fetch doctor availability. Please try again.');
      setTimeSlots([]);
      setSpecificTimes([]);
    } finally {
      setLoading(false);
    }
  };

  const generateSpecificTimes = (slot) => {
    if (!slot) return [];
    const [start, end] = slot.split(' - ').map(time => {
      const [hourMinute, period] = time.trim().split(' ');
      const [hour, minute] = hourMinute.split(':').map(Number);
      const isPM = period === 'PM' && hour !== 12;
      const militaryHour = isPM ? hour + 12 : (period === 'AM' && hour === 12 ? 0 : hour);
      return militaryHour * 60 + minute;
    });

    const intervals = [];
    for (let time = start; time < end; time += 10) {
      const hour = Math.floor(time / 60);
      const minute = time % 60;
      const formattedHour = hour % 12 === 0 ? 12 : hour % 12;
      const period = hour >= 12 ? 'PM' : 'AM';
      const formattedTime = `${formattedHour}:${minute.toString().padStart(2, '0')} ${period}`;
      intervals.push(formattedTime);
    }
    return intervals;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'doctor' || name === 'date') {
      setFormData(prev => ({ ...prev, timeSlot: '', specificTime: '' }));
      setSpecificTimes([]);
    } else if (name === 'timeSlot') {
      const intervals = generateSpecificTimes(value);
      setSpecificTimes(intervals);
      setFormData(prev => ({ ...prev, specificTime: '' }));
    }
  };

  const handleStateChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      state: value,
      city: '',
      doctor: '',
      date: '',
      timeSlot: '',
      specificTime: '',
    }));
    setCities(stateCityData[value] || []);
    setDoctors([]);
    setAvailability({});
    setTimeSlots([]);
    setSpecificTimes([]);
  };

  const handleCityChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      city: value,
      doctor: '',
      date: '',
      timeSlot: '',
      specificTime: '',
    }));
    fetchDoctors(formData.state, value);
    setAvailability({});
    setTimeSlots([]);
    setSpecificTimes([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!patientUser) {
      setError("Please log in to book an appointment.");
      return;
    }
    setLoading(true);
    setError('');
    try {
      if (!formData.specificTime) throw new Error('Please select a specific time slot.');

      const [time, period] = formData.specificTime.split(' ');
      const [hoursStr, minutesStr] = time.split(':');
      const hours = parseInt(hoursStr, 10);
      const minutes = parseInt(minutesStr, 10);
      let formattedHours = hours;
      if (period === 'PM' && hours !== 12) formattedHours += 12;
      else if (period === 'AM' && hours === 12) formattedHours = 0;

      const dateString = `${formData.date}T${formattedHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
      const appointmentData = {
        patientId: patientUser.id,
        name: formData.name,
        age: parseInt(formData.age, 10),
        gender: formData.gender,
        doctorId: formData.doctor,
        date: dateString,
        symptoms: formData.notes,
      };

      const selectedDoctor = doctors.find(doc => doc._id === formData.doctor);
      const doctorFee = selectedDoctor?.consultationFee || 0;
      const totalFee = doctorFee * (1 + platformFeeRate);

      navigate('/review-appointment', {
        state: { appointmentData, doctorFee, totalFee, selectedDoctor, platformFeeRate }
      });
    } catch (err) {
      setError(err.message || 'Failed to process appointment.');
    } finally {
      setLoading(false);
    }
  };

  if (!patientUser || !patientUser.id) {
    return (
      <div className="book-appointment-container">
        <div className="book-appointment-card">
          <h2>Error</h2>
          <p>Please log in to book an appointment.</p>
          <button onClick={() => navigate('/login?type=patient')}>Go to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      
      <div className="book-appointment-container">
        <div className="book-appointment-card">
          <h2>Book an Appointment</h2>
          {error && <p className="error-text">{error}</p>}
          <p className="subtitle">Fill in your details to schedule your appointment.</p>
          <form onSubmit={handleSubmit} className="appointment-form">
            <div className="form-group">
              <label htmlFor="name">Full Name:</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="age">Age:</label>
              <input
                type="number"
                id="age"
                name="age"
                value={formData.age}
                onChange={handleChange}
                placeholder="Enter your age"
                required
                min="1"
                max="120"
              />
            </div>
            <div className="form-group">
              <label htmlFor="gender">Gender:</label>
              <select id="gender" name="gender" value={formData.gender} onChange={handleChange} required>
                <option value="">Select gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="state">Select State:</label>
              <select id="state" name="state" value={formData.state} onChange={handleStateChange} required>
                <option value="">Choose a state</option>
                {Object.keys(stateCityData).map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="city">Select City:</label>
              <select
                id="city"
                name="city"
                value={formData.city}
                onChange={handleCityChange}
                required
                disabled={!formData.state}
              >
                <option value="">Choose a city</option>
                {cities.map(city => <option key={city} value={city}>{city}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="doctor">Select Doctor:</label>
              <select
                id="doctor"
                name="doctor"
                value={formData.doctor}
                onChange={handleChange}
                required
                disabled={!formData.city || loading}
              >
                <option value="">Choose a doctor</option>
                {doctors.map(doctor => {
                  const totalFee = (doctor.consultationFee * (1 + platformFeeRate)).toFixed(2);
                  return (
                    <option key={doctor._id} value={doctor._id}>
                      {doctor.name} - {doctor.specialization} ({doctor.experience} years) ₹{totalFee}
                    </option>
                  );
                })}
              </select>
              {loading && <p className="loading-text">Loading doctors...</p>}
            </div>
            <div className="form-group">
              <label htmlFor="date">Date:</label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="form-group">
              <label htmlFor="timeSlot">Time Slot:</label>
              <select
                id="timeSlot"
                name="timeSlot"
                value={formData.timeSlot}
                onChange={handleChange}
                required
                disabled={loading || !timeSlots.length}
              >
                <option value="">Select a time slot</option>
                {timeSlots.map(slot => (
                  <option key={slot} value={slot}>{slot}</option>
                ))}
              </select>
              {!loading && formData.doctor && formData.date && !timeSlots.length && (
                <p className="no-slots">No available slots for this date.</p>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="specificTime">Select Specific Time:</label>
              <select
                id="specificTime"
                name="specificTime"
                value={formData.specificTime}
                onChange={handleChange}
                required
                disabled={loading || !specificTimes.length}
              >
                <option value="">Choose a specific time</option>
                {specificTimes.map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
              {!loading && formData.timeSlot && !specificTimes.length && (
                <p className="no-slots">No specific times available.</p>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="notes">Additional Notes:</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="4"
                placeholder="Enter any additional notes..."
              />
            </div>
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? 'Booking...' : 'Review Appointment'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookAppointment;